import { useEffect, useRef, useState } from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

/** Render an authenticated private PDF without navigating the sandboxed PDF response. */
export function PrivatePdfPreview({ documentId, fileName }: { documentId: number; fileName: string }) {
  const host = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState("Cargando vista previa privada…");

  useEffect(() => {
    const controller = new AbortController();
    let loadingTask: ReturnType<typeof getDocument> | undefined;
    let cancelled = false;
    async function render() {
      setStatus("Cargando vista previa privada…");
      try {
        const response = await fetch(`/api/commercial-request-documents/${documentId}/download?preview=1`, {
          credentials: "same-origin", signal: controller.signal,
        });
        if (!response.ok) throw new Error("PDF_PREVIEW_FAILED");
        const data = new Uint8Array(await response.arrayBuffer());
        if (cancelled) return;
        loadingTask = getDocument({ data });
        const pdf = await loadingTask.promise;
        if (cancelled || !host.current) return;
        host.current.replaceChildren();
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          if (cancelled || !host.current) return;
          const viewport = page.getViewport({ scale: Math.min(1.5, Math.max(0.75, (host.current.clientWidth || 640) / page.getViewport({ scale: 1 }).width)) });
          const canvas = window.document.createElement("canvas");
          canvas.setAttribute("aria-label", `Página ${pageNumber} de ${pdf.numPages}`);
          canvas.className = "mx-auto mb-3 h-auto max-w-full rounded border bg-white";
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          host.current.append(canvas);
          await page.render({ canvasContext: canvas.getContext("2d")!, viewport }).promise;
        }
        if (!cancelled) setStatus(`${pdf.numPages} ${pdf.numPages === 1 ? "página" : "páginas"} · documento privado`);
      } catch {
        if (!cancelled) setStatus("No se pudo mostrar la vista previa. El original privado sigue disponible para abrir.");
      }
    }
    void render();
    return () => { cancelled = true; controller.abort(); void loadingTask?.destroy(); };
  }, [documentId]);

  return <section className="mt-2 space-y-2" aria-label={`Vista previa privada: ${fileName}`}>
    <p role="status" aria-live="polite" className="text-xs text-muted-foreground">{status}</p>
    <div ref={host} className="max-h-[36rem] overflow-auto rounded border bg-muted/20 p-2" />
  </section>;
}
